import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Upload, Send, CheckCircle2, Loader2, AlertCircle } from 'lucide-react';
import { contactInfo, servicesData } from '../data/mock';

const FORMSUBMIT_ENDPOINT = 'https://formsubmit.co/ajax/sales@archeengineeringsolutions.com';

export default function Contact() {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    projectDescription: '',
    timeline: '',
    budget: ''
  });

  const [status, setStatus] = useState({ state: 'idle', message: '' });
  const [errors, setErrors] = useState({});

  const validate = () => {
    const errs = {};
    if (!formData.name.trim()) errs.name = 'Name is required';
    if (!formData.email.trim()) errs.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) errs.email = 'Enter a valid email';
    if (!formData.phone.trim()) errs.phone = 'Phone is required';
    if (!formData.projectDescription.trim()) errs.projectDescription = 'Project description is required';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setStatus({ state: 'submitting', message: '' });

    const submissionDate = new Date().toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata',
      dateStyle: 'full',
      timeStyle: 'short'
    });

    const payload = {
      _subject: `New Quote Request from ${formData.name}`,
      _template: 'table',
      _captcha: 'false',
      'Customer Name': formData.name,
      'Company Name': formData.company || 'Not provided',
      'Email Address': formData.email,
      'Phone Number': formData.phone,
      'Service Required': formData.service || 'Not specified',
      'Timeline': formData.timeline || 'Not specified',
      'Budget': formData.budget || 'Not specified',
      'Message': formData.projectDescription,
      'Submission Date & Time': submissionDate
    };

    try {
      const response = await fetch(FORMSUBMIT_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        setStatus({
          state: 'success',
          message: 'Thank you for contacting ARCHE Engineering Solutions. Our team will get back to you shortly.'
        });
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          service: '',
          projectDescription: '',
          timeline: '',
          budget: ''
        });
      } else {
        throw new Error('Submission failed');
      }
    } catch (err) {
      setStatus({
        state: 'error',
        message: 'Something went wrong. Please try again or email us directly at sales@archeengineeringsolutions.com'
      });
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: undefined }));
  };

  return (
    <section ref={ref} className="relative py-32 overflow-hidden" id="contact">
      {/* Background Elements */}
      <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-full h-full bg-gradient-to-b from-[#2A4A85]/10 via-[#4A6FA5]/5 to-transparent" />

      <div className="container mx-auto px-6 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <div className="text-[#6B8FCC]/70 font-mono text-sm mb-4 tracking-widest">CONTACT</div>
          <h2 className="text-5xl md:text-7xl font-black mb-6 bg-gradient-to-r from-white via-[#B8CDE8] to-white bg-clip-text text-transparent">
            Request a Quote
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Share your project details and get a detailed quote from our engineering team.
          </p>
        </motion.div>

        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            {status.state === 'success' ? (
              <div
                data-testid="form-success-message"
                className="p-8 rounded-3xl bg-green-500/10 backdrop-blur-xl border border-green-500/30 text-center"
              >
                <CheckCircle2 className="w-16 h-16 text-green-400 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-white mb-3">Message Sent Successfully</h3>
                <p className="text-gray-300 leading-relaxed">{status.message}</p>
                <Button
                  onClick={() => setStatus({ state: 'idle', message: '' })}
                  data-testid="send-another-btn"
                  className="mt-6 bg-white/5 hover:bg-white/10 text-white border border-white/10 rounded-xl"
                >
                  Send Another Request
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="quote-form">
                {/* Name & Email */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">Name *</label>
                    <Input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      data-testid="contact-name-input"
                      className={`bg-white/5 border-white/10 focus:border-[#4A6FA5] text-white ${errors.name ? 'border-red-500/60' : ''}`}
                      placeholder="Your name"
                    />
                    {errors.name && <p className="text-xs text-red-400 mt-1">{errors.name}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">Email *</label>
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      data-testid="contact-email-input"
                      className={`bg-white/5 border-white/10 focus:border-[#4A6FA5] text-white ${errors.email ? 'border-red-500/60' : ''}`}
                      placeholder="your@email.com"
                    />
                    {errors.email && <p className="text-xs text-red-400 mt-1">{errors.email}</p>}
                  </div>
                </div>

                {/* Phone & Company */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">Phone *</label>
                    <Input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      data-testid="contact-phone-input"
                      className={`bg-white/5 border-white/10 focus:border-[#4A6FA5] text-white ${errors.phone ? 'border-red-500/60' : ''}`}
                      placeholder="+91 98765 43210"
                    />
                    {errors.phone && <p className="text-xs text-red-400 mt-1">{errors.phone}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">Company</label>
                    <Input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      data-testid="contact-company-input"
                      className="bg-white/5 border-white/10 focus:border-[#4A6FA5] text-white"
                      placeholder="Company name"
                    />
                  </div>
                </div>

                {/* Service Required */}
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">Service Required</label>
                  <select
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    data-testid="contact-service-select"
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 focus:border-[#4A6FA5] text-white rounded-md text-sm outline-none"
                  >
                    <option value="" className="bg-[#050505]">Select a service</option>
                    {servicesData.map(s => (
                      <option key={s.id} value={s.title} className="bg-[#050505]">{s.title}</option>
                    ))}
                    <option value="Other" className="bg-[#050505]">Other / Multiple Services</option>
                  </select>
                </div>

                {/* Project Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">Message / Project Description *</label>
                  <Textarea
                    name="projectDescription"
                    value={formData.projectDescription}
                    onChange={handleChange}
                    data-testid="contact-message-input"
                    rows={4}
                    className={`bg-white/5 border-white/10 focus:border-[#4A6FA5] text-white resize-none ${errors.projectDescription ? 'border-red-500/60' : ''}`}
                    placeholder="Describe your project, quantities, materials, and any specific requirements..."
                  />
                  {errors.projectDescription && <p className="text-xs text-red-400 mt-1">{errors.projectDescription}</p>}
                </div>

                {/* Timeline & Budget */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">Timeline</label>
                    <Input
                      type="text"
                      name="timeline"
                      value={formData.timeline}
                      onChange={handleChange}
                      data-testid="contact-timeline-input"
                      className="bg-white/5 border-white/10 focus:border-[#4A6FA5] text-white"
                      placeholder="e.g., 2-3 weeks"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">Budget Range</label>
                    <Input
                      type="text"
                      name="budget"
                      value={formData.budget}
                      onChange={handleChange}
                      data-testid="contact-budget-input"
                      className="bg-white/5 border-white/10 focus:border-[#4A6FA5] text-white"
                      placeholder="e.g., ₹50,000 - ₹1,00,000"
                    />
                  </div>
                </div>

                {/* Error Message */}
                {status.state === 'error' && (
                  <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/30 flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-300">{status.message}</p>
                  </div>
                )}

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={status.state === 'submitting'}
                  data-testid="submit-quote-btn"
                  className="w-full py-6 text-lg font-semibold bg-gradient-to-r from-[#1E3A6F] to-[#2A4A85] hover:from-[#2A4A85] hover:to-[#4A6FA5] text-white border-0 rounded-xl transition-all duration-300 hover:scale-[1.01] hover:shadow-2xl hover:shadow-blue-900/50 disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  <span className="flex items-center justify-center gap-2">
                    {status.state === 'submitting' ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        Send Quote Request
                      </>
                    )}
                  </span>
                </Button>
              </form>
            )}
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            {/* Info Card */}
            <div className="p-8 rounded-3xl bg-white/5 backdrop-blur-xl border border-white/10">
              <h3 className="text-2xl font-bold mb-6 text-white">Contact Information</h3>

              <div className="space-y-6">
                <div>
                  <div className="text-sm text-gray-500 mb-1">Email</div>
                  <a
                    href={`mailto:${contactInfo.email}`}
                    data-testid="contact-email-link"
                    className="text-[#6B8FCC] hover:text-[#8FAEE0] transition-colors break-all"
                  >
                    {contactInfo.email}
                  </a>
                </div>

                <div>
                  <div className="text-sm text-gray-500 mb-1">Phone</div>
                  <a
                    href={`tel:${contactInfo.phoneRaw}`}
                    data-testid="contact-phone-link"
                    className="text-[#6B8FCC] hover:text-[#8FAEE0] transition-colors"
                  >
                    {contactInfo.phone}
                  </a>
                </div>

                <div>
                  <div className="text-sm text-gray-500 mb-1">Address</div>
                  <address className="text-white not-italic leading-relaxed" data-testid="contact-address">
                    {contactInfo.addressLines.map((line, idx) => (
                      <div key={idx}>{line}</div>
                    ))}
                  </address>
                </div>

                <div>
                  <div className="text-sm text-gray-500 mb-1">Business Hours</div>
                  <p className="text-white">{contactInfo.hours}</p>
                </div>
              </div>
            </div>

            {/* Why Choose Card */}
            <div className="p-8 rounded-3xl bg-gradient-to-br from-[#4A6FA5]/10 to-[#2A4A85]/10 backdrop-blur-xl border border-[#4A6FA5]/30">
              <h3 className="text-xl font-bold mb-4 text-white">Why Get a Quote?</h3>
              <ul className="space-y-3 text-gray-300">
                <li className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 bg-[#6B8FCC] rounded-full mt-2" />
                  <span>Detailed cost breakdown within 24 hours</span>
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 bg-[#6B8FCC] rounded-full mt-2" />
                  <span>DFM analysis and recommendations</span>
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 bg-[#6B8FCC] rounded-full mt-2" />
                  <span>Material and process suggestions</span>
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 bg-[#6B8FCC] rounded-full mt-2" />
                  <span>No obligation, completely free</span>
                </li>
              </ul>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
